import { useEffect, useState } from 'react';
import {
  loadBroadcastManagers,
  loadBroadcastState,
  removeBroadcastManager,
  setBroadcastEntryEnabled,
  setBroadcastManager,
  startBroadcast,
  stopBroadcast,
  updateBroadcast,
} from '../api/broadcastApi.js';

const EMPTY_MANAGER = { userId: '', displayName: '', dcNickname: '', arcaNickname: '' };

function formatStartedAt(value) {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('ko-KR', {
      month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return '';
  }
}

export function BroadcastManagementBoard({ mobile = false }) {
  const [state, setState] = useState(null);
  const [managers, setManagers] = useState([]);
  const [form, setForm] = useState({ url: '', title: '' });
  const [manager, setManager] = useState(EMPTY_MANAGER);
  const [working, setWorking] = useState('');
  const [notice, setNotice] = useState({ type: '', message: '' });

  const refresh = async ({ quiet = false } = {}) => {
    if (!quiet) setWorking('refresh');
    try {
      const [nextState, managerResult] = await Promise.all([loadBroadcastState(), loadBroadcastManagers()]);
      setState(nextState);
      setManagers(managerResult.managers || []);
      if (!quiet) setNotice({ type: '', message: '' });
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || '중계방 정보를 불러오지 못했습니다.' });
    } finally {
      if (!quiet) setWorking('');
    }
  };

  useEffect(() => { refresh({ quiet: true }); }, []);

  const begin = async () => {
    if (working) return;
    if (!form.url.trim()) {
      setNotice({ type: 'error', message: '유튜브 주소를 입력해주세요.' });
      return;
    }
    // 중계를 켜면 들어와 있는 모두에게 즉시 영상이 뜬다. 되돌리려면 종료를 눌러야 한다.
    const message = live
      ? `새 방송으로 시작합니다.\n제목: ${form.title.trim() || '(없음)'}\n\n방송 기록이 새로 남습니다. 주소만 고치려면 '영상 교체'를 쓰세요.\n\n계속할까요?`
      : `중계를 시작합니다.\n제목: ${form.title.trim() || '(없음)'}\n\n중계방에 있는 모두에게 바로 표시됩니다. 계속할까요?`;
    if (!window.confirm(message)) return;
    setWorking('start');
    try {
      const result = await startBroadcast({ url: form.url.trim(), title: form.title.trim() });
      setState(result.state || null);
      setNotice({ type: 'success', message: '중계를 시작했습니다.' });
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || '중계를 시작하지 못했습니다.' });
    } finally {
      setWorking('');
    }
  };

  // 주소 오타를 고치는 건 흔한 일인데, 중계 시작으로 처리하면 그때마다 방송 기록이 하나씩 더
  // 생긴다. 세션은 그대로 두고 영상·제목만 갈아끼운다.
  const replace = async () => {
    if (working) return;
    if (!form.url.trim() && !form.title.trim()) {
      setNotice({ type: 'error', message: '교체할 주소나 제목을 입력해주세요.' });
      return;
    }
    setWorking('update');
    try {
      const result = await updateBroadcast({ url: form.url.trim(), title: form.title.trim() });
      setState(result.state || null);
      setNotice({ type: 'success', message: '영상을 교체했습니다. 보고 있는 사람 화면도 바로 바뀝니다.' });
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || '영상을 교체하지 못했습니다.' });
    } finally {
      setWorking('');
    }
  };

  const end = async () => {
    if (working) return;
    if (!window.confirm('중계를 종료할까요?\n\n보고 있는 모두의 화면에서 영상이 즉시 내려갑니다.')) return;
    setWorking('stop');
    try {
      const result = await stopBroadcast();
      setState(result.state || null);
      setNotice({ type: 'success', message: '중계를 종료했습니다.' });
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || '중계를 종료하지 못했습니다.' });
    } finally {
      setWorking('');
    }
  };

  const toggleEntry = async () => {
    if (working) return;
    const next = !(state?.entryEnabled !== false);
    if (!next && !window.confirm('중계방 입장을 닫을까요?\n\n입장 버튼이 사라지고, 매니저가 아닌 접속자는 연결이 끊깁니다.')) return;
    setWorking('entry');
    try {
      const result = await setBroadcastEntryEnabled(next);
      setState(result.state || null);
      setNotice({ type: 'success', message: next ? '중계방 입장을 열었습니다.' : '중계방 입장을 닫았습니다.' });
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || '입장 설정을 바꾸지 못했습니다.' });
    } finally {
      setWorking('');
    }
  };

  const saveManager = async () => {
    if (working) return;
    if (!manager.userId.trim() || !manager.displayName.trim()) {
      setNotice({ type: 'error', message: '계정 UUID와 공개 이름을 입력해주세요.' });
      return;
    }
    setWorking('manager');
    try {
      const result = await setBroadcastManager({
        userId: manager.userId.trim(),
        displayName: manager.displayName.trim(),
        dcNickname: manager.dcNickname.trim(),
        arcaNickname: manager.arcaNickname.trim(),
      });
      setManagers(result.managers || []);
      setManager(EMPTY_MANAGER);
      setNotice({ type: 'success', message: '매니저를 지정했습니다.' });
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || '매니저를 지정하지 못했습니다.' });
    } finally {
      setWorking('');
    }
  };

  const dropManager = async (entry) => {
    if (working) return;
    if (!window.confirm(`'${entry.displayName}' 매니저 지정을 해제할까요?`)) return;
    setWorking(`manager-${entry.userId}`);
    try {
      const result = await removeBroadcastManager(entry.userId);
      setManagers(result.managers || []);
      setNotice({ type: 'success', message: '매니저 지정을 해제했습니다.' });
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || '매니저를 해제하지 못했습니다.' });
    } finally {
      setWorking('');
    }
  };

  const live = state?.isLive === true;

  return (
    <section className={`broadcast-management ${mobile ? 'mobile' : 'desktop'}`} aria-label="중계방 관리">
      <header>
        <div>
          <h2>중계방 관리</h2>
          <p>유튜브 방송을 중계방에 띄우고 중계방 매니저를 지정합니다. 기존 유니온 채팅과는 분리된 방입니다.</p>
        </div>
        <button type="button" className="secondary" disabled={Boolean(working)} onClick={() => refresh()}>
          {working === 'refresh' ? '새로고침 중…' : '새로고침'}
        </button>
      </header>

      <div className="broadcast-management-status">
        <div><span>상태</span><strong className={live ? 'live' : ''}>{live ? '🔴 중계 중' : '중계 없음'}</strong></div>
        <div><span>접속자</span><strong>{Number(state?.viewerCount || 0).toLocaleString('ko-KR')}명</strong></div>
        <div><span>제목</span><strong>{state?.title || '—'}</strong></div>
        <div><span>시작</span><strong>{formatStartedAt(state?.startedAt) || '—'}</strong></div>
        <div className="broadcast-management-entry-toggle">
          <span>입장</span>
          <strong className={state?.entryEnabled === false ? 'closed' : ''}>{state?.entryEnabled === false ? '닫힘' : '열림'}</strong>
          <button type="button" disabled={Boolean(working)} onClick={toggleEntry}>
            {working === 'entry' ? '바꾸는 중…' : state?.entryEnabled === false ? '입장 열기' : '입장 닫기'}
          </button>
        </div>
      </div>

      <div className="broadcast-management-form">
        <label>
          <span>유튜브 주소</span>
          <input
            value={form.url}
            placeholder="https://youtube.com/live/... 또는 watch?v=..."
            onChange={(event) => setForm((current) => ({ ...current, url: event.target.value }))}
          />
        </label>
        <label>
          <span>방송 제목</span>
          <input
            value={form.title}
            maxLength={120}
            placeholder="니케 공식 방송 같이보기"
            onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
          />
        </label>
        <div className="broadcast-management-actions">
          <button type="button" className="apply" disabled={Boolean(working) || !live} onClick={replace}>
            {working === 'update' ? '교체 중…' : '영상 교체'}
          </button>
          <button type="button" disabled={Boolean(working)} onClick={begin}>
            {working === 'start' ? '시작 중…' : live ? '새 방송 시작' : '중계 시작'}
          </button>
          <button type="button" className="stop" disabled={Boolean(working) || !live} onClick={end}>
            {working === 'stop' ? '종료 중…' : '중계 종료'}
          </button>
        </div>
        <small>
          주소에서 영상 ID만 뽑아 저장합니다. 유튜브가 아닌 주소는 거부됩니다.
          {live ? ' 영상 교체는 지금 방송을 이어가고, 새 방송 시작은 기록을 새로 남깁니다.' : ''}
        </small>
      </div>

      <div className="broadcast-management-managers">
        <header>
          <strong>중계방 매니저</strong>
          <p>일반 참여자는 모두 익명입니다. 매니저만 이름과 DC·아카라이브 닉네임이 공개됩니다.</p>
          {/* 지정하면 방송을 켜고 끌 수 있게 되므로, 누르기 전에 보이는 자리에 적어 둔다. */}
          <p>매니저는 채팅 관리(삭제·금지·차단)와 <strong>중계 시작·종료·영상 교체</strong>를 할 수 있습니다. 입장 열기/닫기와 매니저 지정은 플랫폼 관리자만 가능합니다.</p>
        </header>

        <div className="broadcast-management-manager-form">
          <label><span>계정 UUID</span><input value={manager.userId} placeholder="계정·밴 관리에서 확인" onChange={(event) => setManager((current) => ({ ...current, userId: event.target.value }))} /></label>
          <label><span>공개 이름</span><input value={manager.displayName} maxLength={40} onChange={(event) => setManager((current) => ({ ...current, displayName: event.target.value }))} /></label>
          <label><span>DC 닉네임</span><input value={manager.dcNickname} maxLength={40} onChange={(event) => setManager((current) => ({ ...current, dcNickname: event.target.value }))} /></label>
          <label><span>아카라이브 닉네임</span><input value={manager.arcaNickname} maxLength={40} onChange={(event) => setManager((current) => ({ ...current, arcaNickname: event.target.value }))} /></label>
          <button type="button" disabled={Boolean(working)} onClick={saveManager}>
            {working === 'manager' ? '지정 중…' : '매니저 지정'}
          </button>
        </div>

        <div className="broadcast-management-manager-list">
          {managers.length ? managers.map((entry) => (
            <article key={entry.userId}>
              <div>
                <strong>🛡 {entry.displayName}</strong>
                <span>{[entry.dcNickname && `DC ${entry.dcNickname}`, entry.arcaNickname && `아카 ${entry.arcaNickname}`].filter(Boolean).join(' · ') || '연동 닉네임 없음'}</span>
              </div>
              <button type="button" disabled={Boolean(working)} onClick={() => dropManager(entry)}>지정 해제</button>
            </article>
          )) : <p>지정된 매니저가 없습니다.</p>}
        </div>
      </div>

      {notice.message ? <p className={`broadcast-management-notice ${notice.type}`} role="status">{notice.message}</p> : null}
    </section>
  );
}
